// Pure helpers for bracket seeding + side detection.
//
// Kept isolated from React state so they can be unit-tested and reused
// between the Quick Setup view (which generates round-1 matches) and the
// validation pass in useBracketPopulation (which scopes duplicate-seed
// detection per side).

// Standard power-of-two bracket seeding order. Given n seeds, returns the
// seed number for each bracket position so that the top seed and second seed
// end up on opposite halves and only meet in the final.
//
// Example for n=8: [1, 8, 4, 5, 2, 7, 3, 6] — which produces round-1 pairs
// (1,8), (4,5), (2,7), (3,6). Seeds 1 and 2 are in opposite halves.
//
// n must be a power of two >= 1. Caller is responsible for validation.
export function seedingOrder(n) {
  if (n <= 1) return [1]
  const prev = seedingOrder(n / 2)
  const out = []
  for (const s of prev) out.push(s, n + 1 - s)
  return out
}

// Determine which "side" of the bracket a match is on. Brackets split in half
// at the final — the left half feeds into one semifinal, the right into the
// other — and duplicate seeds are legal across sides (e.g. seed 4 on each
// side of a two-sided bracket). This helper lets validation scope duplicate
// checks per side so we don't flag cross-side duplicates as conflicts.
//
// PQ matches (round 0) inherit the side of their feeds-into target. Rounds
// > 1 are resolved to a round-1 descendant before bucketing.
//
// Returns 'single' when there's only one round-1 match (nothing to split),
// otherwise 'left' or 'right'.
export function computeSideKey(match, round1MatchCount) {
  if (round1MatchCount <= 1) return 'single'
  const half = Math.ceil(round1MatchCount / 2)

  let r1Number
  if (match.round === 0) {
    const parentR = match.feedsIntoRound || 1
    const parentN = match.feedsIntoMatchNumber || 1
    r1Number = parentR === 1
      ? parentN
      : Math.pow(2, parentR - 1) * (parentN - 1) + 1
  } else if (match.round === 1) {
    r1Number = match.matchNumber
  } else {
    // Round 2+: use the smallest round-1 match number in this subtree.
    r1Number = Math.pow(2, match.round - 1) * (match.matchNumber - 1) + 1
  }

  return r1Number <= half ? 'left' : 'right'
}
